import type { Dispatch, FormEvent, SetStateAction } from 'react';
import { useCallback, useState } from 'react';
import {
  notificationAdminService,
  NotificationAdminTestResponse,
} from '../../services/NotificationAdminService';

export interface AdminNotificationUiStatus {
  message: string;
  isError: boolean;
}

interface AdminNotificationValues {
  emailTo: string;
  emailSubject: string;
  emailText: string;
  inAppSubject: string;
  inAppText: string;
}

interface UseAdminNotificationTestFormsResult {
  emailTo: string;
  setEmailTo: Dispatch<SetStateAction<string>>;
  emailSubject: string;
  setEmailSubject: Dispatch<SetStateAction<string>>;
  emailText: string;
  setEmailText: Dispatch<SetStateAction<string>>;
  sendingEmail: boolean;
  inAppSubject: string;
  setInAppSubject: Dispatch<SetStateAction<string>>;
  inAppText: string;
  setInAppText: Dispatch<SetStateAction<string>>;
  sendingInApp: boolean;
  status: AdminNotificationUiStatus | null;
  submitEmailTest: (event: FormEvent) => Promise<void>;
  submitInAppTest: (event: FormEvent) => Promise<void>;
}

const DEFAULT_VALUES: AdminNotificationValues = {
  emailTo: '',
  emailSubject: 'Test notification e-mail',
  emailText: 'Ceci est un test e-mail depuis la page admin.',
  inAppSubject: 'Test notification admin',
  inAppText: 'Ceci est un test in-app sur le compte admin connecte.',
};

const buildEmailSuccessMessage = (response: NotificationAdminTestResponse): string =>
  `Test e-mail envoye (${response.dryRun ? 'dry-run' : 'reel'}).`;

const buildInAppSuccessMessage = (response: NotificationAdminTestResponse): string =>
  `Notification in-app envoyee sur votre compte admin${response.detail ? ` (${response.detail})` : '.'}`;

const readErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return fallbackMessage;
};

export const useAdminNotificationTestForms = (): UseAdminNotificationTestFormsResult => {
  const [emailTo, setEmailTo] = useState(DEFAULT_VALUES.emailTo);
  const [emailSubject, setEmailSubject] = useState(DEFAULT_VALUES.emailSubject);
  const [emailText, setEmailText] = useState(DEFAULT_VALUES.emailText);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [inAppSubject, setInAppSubject] = useState(DEFAULT_VALUES.inAppSubject);
  const [inAppText, setInAppText] = useState(DEFAULT_VALUES.inAppText);
  const [sendingInApp, setSendingInApp] = useState(false);
  const [status, setStatus] = useState<AdminNotificationUiStatus | null>(null);

  const submitEmailTest = useCallback(async (event: FormEvent) => {
    event.preventDefault();
    setStatus(null);
    setSendingEmail(true);

    try {
      const response = await notificationAdminService.sendTest({
        channel: 'email',
        to: emailTo.trim(),
        subject: emailSubject.trim(),
        text: emailText.trim(),
      });
      setStatus({
        isError: false,
        message: buildEmailSuccessMessage(response),
      });
    } catch (error) {
      setStatus({
        isError: true,
        message: readErrorMessage(error, 'Echec de l envoi du test e-mail.'),
      });
    } finally {
      setSendingEmail(false);
    }
  }, [emailSubject, emailText, emailTo]);

  const submitInAppTest = useCallback(async (event: FormEvent) => {
    event.preventDefault();
    setStatus(null);
    setSendingInApp(true);

    try {
      const response = await notificationAdminService.sendTest({
        channel: 'in_app',
        subject: inAppSubject.trim(),
        text: inAppText.trim(),
      });
      setStatus({
        isError: false,
        message: buildInAppSuccessMessage(response),
      });
    } catch (error) {
      setStatus({
        isError: true,
        message: readErrorMessage(error, 'Echec de l envoi du test in-app.'),
      });
    } finally {
      setSendingInApp(false);
    }
  }, [inAppSubject, inAppText]);

  return {
    emailTo,
    setEmailTo,
    emailSubject,
    setEmailSubject,
    emailText,
    setEmailText,
    sendingEmail,
    inAppSubject,
    setInAppSubject,
    inAppText,
    setInAppText,
    sendingInApp,
    status,
    submitEmailTest,
    submitInAppTest,
  };
};

export default useAdminNotificationTestForms;
